/**
 * MER-212 / MER-486: pure mapping + formatting for the commercial
 * state panel on the admin customer detail page. Source of truth is
 * the staff-only public.tenant_commercial table (migration 0039); the
 * original bundled-YAML source was retired before any values shipped
 * because /manifests/*.yaml is publicly fetchable (AJ decision on
 * MER-185, 2026-06-10).
 *
 * Plain .mjs so the node:test suite imports it directly (the
 * overviewSummary.mjs precedent). All columns optional; a missing row
 * renders the panel's "Not configured" state.
 */

/** PostgREST serializes date columns as "YYYY-MM-DD" strings; be
 * tolerant of Date objects and full timestamps anyway. */
function toIsoDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (m && Number.isFinite(Date.parse(m[1]))) return m[1];
  }
  return null;
}

/** numeric columns can surface as number or numeric-string. */
function toNonNegativeNumber(value) {
  const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) && n >= 0 ? n : null;
}

function toTrimmedString(value) {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * Normalize a tenant_commercial row into the panel's shape, or null
 * when there is no row.
 *
 * @param {unknown} row
 * @returns {{
 *   partnerContact: { name: string | null, email: string | null, role: string | null } | null,
 *   mrrUsd: number | null,
 *   contractStart: string | null,
 *   contractEnd: string | null,
 *   notes: string | null,
 * } | null}
 */
export function commercialFromRow(row) {
  if (row === null || row === undefined || typeof row !== "object" || Array.isArray(row)) {
    return null;
  }
  const name = toTrimmedString(row.partner_name);
  const email = toTrimmedString(row.partner_email);
  const role = toTrimmedString(row.partner_role);
  return {
    partnerContact: name || email || role ? { name, email, role } : null,
    mrrUsd: toNonNegativeNumber(row.mrr_usd),
    contractStart: toIsoDate(row.contract_start),
    contractEnd: toIsoDate(row.contract_end),
    notes: toTrimmedString(row.notes),
  };
}

/** 3500 -> "$3,500/mo". Null in, null out. */
export function formatMrr(mrrUsd) {
  if (typeof mrrUsd !== "number" || !Number.isFinite(mrrUsd)) return null;
  return `$${Math.round(mrrUsd).toLocaleString("en-US")}/mo`;
}

/**
 * Human countdown to contract end: "157 days remaining",
 * "Expires today", "Expired 12 days ago". Null when no end date.
 *
 * @param {string | null} contractEnd   ISO date (YYYY-MM-DD)
 * @param {number} [now]   epoch ms, injectable for tests.
 */
export function contractCountdown(contractEnd, now = Date.now()) {
  if (!contractEnd) return null;
  const end = Date.parse(`${contractEnd}T23:59:59Z`);
  if (!Number.isFinite(end)) return null;
  const days = Math.floor((end - now) / (24 * 60 * 60 * 1000));
  if (days > 0) return `${days} day${days === 1 ? "" : "s"} remaining`;
  if (days === 0) return "Expires today";
  const ago = Math.abs(days);
  return `Expired ${ago} day${ago === 1 ? "" : "s"} ago`;
}
